import type { TTree } from 'components/tree/types'
import {
  useForexTickers,
  type UseForexTickers
} from '@/integrations/findex/hooks/forex/useForexTickers'
import { useSession, type UseSesssion } from '@/modules/auth/hooks/useSession'
import IRClient from '@/modules/ir/apiClient'
import { createAssetMap, createInvestmentMap } from '@/utils/asset/map'
import { useAsyncList } from '@/utils/async/useAsyncList'
import { computedWithControl } from '@vueuse/core'
import { getRefId, type FindexInvestment, type FullInvestment } from 'findef'
import { uniqueBy } from 'math'
import { computed, unref, watch, type MaybeRef } from 'vue'
import { InvestmentDisplay } from '../../components/investment-display'
import { twColor } from '@/utils/style'
import InvestmentInfoTooltip from '../../components/investment-info-tooltip'
import { getUserFullName } from '@/utils/user'
import { FindexClient } from '@/integrations/findex'
import { GenericDisplay } from 'components/generic-display'
import Avatar from 'components/avatar'
import { createTree } from './createTree'

export type UseInvestmentTreeProps = {
  forex?: UseForexTickers
  session?: UseSesssion
  mockInvestment?: MaybeRef<FindexInvestment | FullInvestment | undefined>
  investment?: MaybeRef<FindexInvestment | FullInvestment | null | undefined>;
  realParentId?: MaybeRef<string>;
}

const ICON_COLOR = twColor('gray-400')

const isRoot = (inv: FindexInvestment | FullInvestment) => {
  return !inv.parentId || inv.parentId === '' || inv.parentId === ' '
}

export const useInvestmentTree = (props: UseInvestmentTreeProps) => {
  const forex = props.forex || useForexTickers()
  const session = props.session || useSession()
  const user = computed(() => session.state.user);

  const realParentId = computed(() => unref(props.realParentId));

  const mockInv = computed(() => {
    const mock = unref(props.mockInvestment);
    return mock ? (JSON.parse(JSON.stringify(mock)) as FindexInvestment) : undefined
  });

  const investmentsQuery = useAsyncList(async () => {
    const investments = (await IRClient.getInvestments()).filter((it) => !!it.asset)
    console.log('investmentsQuery()')
    const mock = mockInv.value;

    if (mock) {
      return investments.map((it) => {
        const id = getRefId(it)
        if (id === mock.parentId) {
          return {
            ...it,
            childrenIds: uniqueBy([...(it.childrenIds || []), getRefId(mock)], (it) => getRefId(it))
          }
        }
        return it
      })
    }

    return investments
  })

  watch(
    () => unref(props.investment)?.parentId,
    () => {
      investmentsQuery.refresh()
    }
  )

  const investments = computed(() => {
    const mock = mockInv.value;
    const mockId = mock ? getRefId(mock) : '';
    const realPid = realParentId.value;
    return uniqueBy(
      [...(investmentsQuery.items.value || []), ...(mock ? [unref(mock)] : [])],
      (it) => getRefId(it)
    ).map((inv) => {

      if (mock && mock.parentId && realPid && realPid !== getRefId(mock.parentId) && getRefId(inv) === realPid) {
        const copy = JSON.parse(JSON.stringify(inv)) as FindexInvestment;
        copy.childrenIds = (copy.childrenIds || []).filter(child => getRefId(child) !== mockId);
        return copy;

      }

      return inv;
    })
  })

  const assets = computed(() => {
    return investments.value.map((it) => it.asset)
  })

  const fullAssetMap = computedWithControl([assets], () => {
    return createAssetMap(assets.value)
  })

  const assetMap = computed(() => {
    if (investmentsQuery.loading.value) return undefined
    const mock = mockInv.value;
    if (mock) {
      const copy = { ...fullAssetMap.value }
      copy[getRefId(mock.asset)] = mock.asset
      return copy
    }
    return fullAssetMap.value
  })

  const fullInvestmentMap = computedWithControl([investments], () => {
    return createInvestmentMap(investments.value)
  })

  const investmentMap = computed(() => {
    if (investmentsQuery.loading.value) return undefined
    const map = fullInvestmentMap.value
    const mock = mockInv.value;
    if (mock) {
      const copy = { ...map }
      copy[getRefId(mock)] = mock
      return copy
    }
    return map
  })

  const rootInvestment = computed(() => {
    const root = unref(mockInv) || unref(props.investment)
    if (!root) return undefined
    if (root.parentId && investmentMap.value) {
      return investmentMap.value[getRefId(root.parentId)]
    }
    return undefined
  })

  const treeInvestments = computed(() => {
    return investments.value.filter((it) => isRoot(it))
  });

  watchEffect(() => console.dir({
    treeInvestments: treeInvestments.value,
    investments: investments.value
  }, { depth: null }))

  const tree = computed((): TTree => {
    const person = user.value
    const name = person ? getUserFullName(person) : '---'
    const imgSrc = person?.avatar ? FindexClient.urlForAttachment(person?.avatar) : undefined
    const invMap = investmentMap.value
    const assMap = assetMap.value

    if (!invMap || !assMap) {
      return {
        name: '...',
        children: [],
        id: '123'
      }
    }

    return createTree({
      rootInvestment: rootInvestment.value,
      name: name,
      investments: treeInvestments.value,
      nodeProps: {
        invMap: invMap,
        assMap: assMap,
        rootInvestment: rootInvestment.value,
        nodeBody: (inv) => {
          return () => (
            <InvestmentDisplay
              iconClass="w-4 h-4"
              showSubtype={false}
              iconColor={ICON_COLOR}
              size="s"
              key={`investment-${getRefId(inv)}`}
              investment={inv}
            />
          )
        },
        tooltipBody: (inv) => () => (
          <InvestmentInfoTooltip
            key={`investment-${getRefId(inv)}-tooltip`}
            investment={inv}
            forex={forex}
          />
        )
      },
      nodeBody: () => () => (
        <GenericDisplay
          size="s"
          title={name}
          image={() => {
            return <Avatar size="xs" fullname={name} colorPreference={person?.color} src={imgSrc} />
          }}
        />
      )
    })
  })

  const isLoading = computed(() => investmentsQuery.loading.value || !user.value)

  return {
    tree,
    isLoading
  }
}
